import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const unitCount = quantity => Math.max(1, Math.floor(Number(quantity) || 1));

export const assetIdsForQuantity = (assetIds, quantity) =>
  Array.from({ length: unitCount(quantity) }, (_, index) => {
    const value = assetIds?.[index];
    return value == null ? '' : String(value);
  });

export const assetIdsPayload = (assetIds, quantity) => {
  const values = assetIdsForQuantity(assetIds, quantity).map(value => value.trim() || null);
  return values.some(Boolean) ? values : null;
};

export const AssetIdEntryModal = ({ open, productName, quantity, initialValues, onClose, onConfirm }) => {
  const [values, setValues] = useState([]);

  useEffect(() => {
    if (open) setValues(assetIdsForQuantity(initialValues, quantity));
  }, [open, initialValues, quantity]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-lg rounded-sm bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-bold uppercase tracking-tight text-slate-900">Enter Asset IDs</h2>
          <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600" aria-label="Close Asset ID entry">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="mb-1 text-sm text-slate-600">
            Product: <span className="font-medium text-slate-900">{productName || '-'}</span>
          </p>
          <p className="mb-4 text-sm text-slate-500">
            Enter one Asset ID for each of the {values.length} unit(s). Leave an entry blank when that unit has no Asset ID.
          </p>
          <div className="max-h-[400px] space-y-2 overflow-y-auto">
            {values.map((value, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="w-8 text-xs text-slate-400">{index + 1}.</span>
                <input
                  type="text"
                  value={value}
                  onChange={event => setValues(current => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}
                  placeholder="Enter Asset ID"
                  data-testid={`asset-modal-input-${index}`}
                  className="flex-1 rounded-sm border border-slate-200 px-3 py-2 font-mono text-sm focus:border-orange-500 focus:outline-none"
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-3 border-t border-slate-200 pt-6">
            <button type="button" onClick={onClose} className="rounded-sm border border-slate-200 px-6 py-2 text-slate-700 hover:bg-slate-50">Cancel</button>
            <button
              type="button"
              onClick={() => onConfirm(values.map(value => value.trim()))}
              data-testid="asset-modal-confirm"
              className="rounded-sm bg-orange-500 px-6 py-2 font-bold uppercase tracking-wider text-white hover:bg-orange-600"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
